import React, { useRef, useState } from "react";
import { Flex, Text, Button, Link } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { useGetCommentThreadsQuery } from "#graphql/supergraphHooks";
import { ControlTowerContext } from "#utils/context";
import { SPACING } from "#utils/spacing";
import { useControlTowerContext } from "../../ControlTowerContext";
import { CommentCard } from "./CommentCard";
import * as styles from "./styles_vanilla.css";
import { LinkedIssueKey } from "./types";

const COMMENTS_PAGE_SIZE = 2;

type LinkedIssueCardProps = {
  linkedIssue: LinkedIssueKey;
  jiraBaseURL: string;
};

export const LinkedIssueCard: React.FC<LinkedIssueCardProps> = ({
  linkedIssue,
  jiraBaseURL,
}) => {
  const context = useControlTowerContext();
  const isCanopy = context === ControlTowerContext.CANOPY;
  const isGPX = context === ControlTowerContext.GPX;

  const { data, loading, fetchMore } = useGetCommentThreadsQuery({
    variables: {
      issueKey: linkedIssue.key,
      startAt: 0,
      maxResults: COMMENTS_PAGE_SIZE,
      includeInternal: !isGPX,
    },
  });

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const checkedCommentIds = useRef(new Set<string>());

  const handleLoadMore = async () => {
    if (!data?.commentThreads) return;

    const currentComments = data.commentThreads.comments;
    const newStartAt = currentComments.length;

    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          issueKey: linkedIssue.key,
          startAt: newStartAt,
          maxResults: COMMENTS_PAGE_SIZE,
          includeInternal: !isGPX,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            commentThreads: {
              ...fetchMoreResult.commentThreads,
              comments: [
                ...prev.commentThreads.comments,
                ...fetchMoreResult.commentThreads.comments,
              ],
            },
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const comments = data?.commentThreads?.comments || [];
  const hasMore = data?.commentThreads?.hasMore || false;
  const total = data?.commentThreads?.total || 0;

  return (
    <div className={styles.linkedIssueCard}>
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        className={styles.linkedIssueHeader}
      >
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          {isCanopy ? (
            <Link
              href={`${jiraBaseURL}/browse/${linkedIssue.key}`}
              shouldOpenInNewTab
              icon="external-link"
              iconPosition="right"
              iconVariant="outline"
              iconSize="small"
              align="left"
            >
              {linkedIssue.key}
            </Link>
          ) : (
            <Text variant="regular-md" color={Colors.Neutral900}>
              {linkedIssue.key}
            </Text>
          )}
          <Text variant="secondary-sm" color={Colors.Neutral600}>
            {comments.length} of {total} communications
          </Text>
        </Flex>
      </Flex>

      {loading && comments.length === 0 ? (
        <Flex alignItems="center" justifyContent="center" className={styles.loadingContainer}>
          <Text variant="regular-sm" color={Colors.Neutral600}>
            Loading communications...
          </Text>
        </Flex>
      ) : comments.length === 0 ? (
        <div className={styles.emptyState}>
          <Text variant="regular-md" color={Colors.Neutral600}>
            No communications found
          </Text>
        </div>
      ) : (
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          <div className={styles.linkedIssueCommentsContainer}>
            {comments.map((comment, index) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                checkedCommentIds={checkedCommentIds}
                isLast={index === comments.length - 1}
              />
            ))}
          </div>

          {hasMore && (
            <Flex justifyContent="center" className={styles.paginationContainer}>
              <Button
                variant="neutral"
                onClick={() => void handleLoadMore()}
                isLoading={isLoadingMore}
                disabled={isLoadingMore}
              >
                Load More
              </Button>
            </Flex>
          )}
        </Flex>
      )}
    </div>
  );
};
